import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    Clock,
    BookOpen,
    Sparkles,
    Send,
    TrendingUp,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import { CATEGORIES, CAT_COLORS, POSTS, TRENDING } from "../data/blogPosts";
import { fetchBlogPosts } from "../lib/cmsApi";

/* ──────────────────────────────────────────────
 *  Page
 * ────────────────────────────────────────────── */

export default function BlogPage() {
    const [cat, setCat] = useState("all");
    const [q, setQ] = useState("");
    const [email, setEmail] = useState("");
    const [posts, setPosts] = useState(POSTS);
    const [categories, setCategories] = useState(CATEGORIES);

    useEffect(() => {
        fetchBlogPosts()
            .then((data) => {
                if (data?.posts?.length) {
                    setPosts(data.posts);
                    if (data.categories?.length) setCategories(data.categories);
                }
            })
            .catch(() => {});
    }, []);

    const filtered = useMemo(() => {
        let list = posts;
        if (cat !== "all") list = list.filter((p) => p.category === cat);
        if (q.trim()) {
            const s = q.toLowerCase();
            list = list.filter(
                (p) =>
                    p.title.toLowerCase().includes(s) ||
                    p.excerpt.toLowerCase().includes(s) ||
                    p.tag.toLowerCase().includes(s),
            );
        }
        return list;
    }, [cat, q, posts]);

    const subscribe = (e) => {
        e.preventDefault();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error("That email doesn't look right.");
            return;
        }
        toast.success("You're in. First issue lands Friday.");
        setEmail("");
    };

    return (
        <div className="pt-28 lg:pt-32" data-testid="blog-page">
            <Toaster position="top-right" theme="dark" richColors />


            {/* ─── Filters + Search ─── */}
            <section className="relative py-6">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                    <div
                        className="md:col-span-8 flex flex-wrap gap-2"
                        data-testid="blog-categories"
                    >
                        {categories.map((c) => {
                            const active = cat === c.id;
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => setCat(c.id)}
                                    data-testid={`blog-cat-${c.id}`}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                                        active
                                            ? "bg-white/[0.08] border-white/20 text-white"
                                            : "bg-white/[0.02] border-white/[0.06] text-zinc-400 hover:bg-white/[0.05]"
                                    }`}
                                >
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-400 animate-pulse-glow" : "bg-zinc-600"}`}
                                    />
                                    {c.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="md:col-span-4 relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search posts…"
                            data-testid="blog-search"
                            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white placeholder:text-zinc-600 font-code transition"
                        />
                    </div>
                </div>
            </section>

            {/* ─── Card grid ─── */}
            <section className="relative py-10 pb-16" data-testid="blog-grid">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <AnimatePresence mode="popLayout">
                        <motion.div
                            key={cat + q}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
                        >
                            {filtered.map((p, i) => (
                                <motion.a
                                    key={p.id}
                                    href={`/blog/${p.slug}`}
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.4,
                                        delay: i * 0.04,
                                    }}
                                    whileHover={{ y: -4 }}
                                    className="group relative flex h-full flex-col rounded-2xl border border-white/[0.06] bg-obsidian-200/70 backdrop-blur-sm overflow-hidden hover:border-white/15 transition-colors"
                                    data-testid={`blog-card-${p.id}`}
                                >
                                    {/* Cover */}
                                    <div className="relative h-40 shrink-0 overflow-hidden">
                                        <div
                                            className={`absolute inset-0 bg-gradient-to-br ${CAT_COLORS[p.category].ring} opacity-90`}
                                        />
                                        <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
                                        <CoverDecor
                                            accent={CAT_COLORS[p.category].accent}
                                        />
                                        <span
                                            className={`absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 ${CAT_COLORS[p.category].chip}`}
                                        >
                                            {p.featured && (
                                                <Sparkles
                                                    className={`w-3 h-3 ${CAT_COLORS[p.category].accent}`}
                                                />
                                            )}
                                            <span
                                                className={`text-[10px] font-code uppercase tracking-[0.18em] font-semibold ${CAT_COLORS[p.category].accent}`}
                                            >
                                                {p.tag}
                                            </span>
                                        </span>
                                        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 font-code text-[10px] text-zinc-300 bg-black/40 border border-white/10 rounded px-2 py-0.5">
                                            <Clock className="w-2.5 h-2.5" />
                                            {p.read}
                                        </span>
                                    </div>

                                    {/* Body */}
                                    <div className="flex flex-1 flex-col p-6">
                                        <h3 className="font-outfit text-lg font-semibold text-white leading-snug line-clamp-2 group-hover:text-cyan-200 transition-colors">
                                            {p.title}
                                        </h3>
                                        <p className="mt-2 flex-1 text-sm text-zinc-400 line-clamp-3 leading-relaxed">
                                            {p.excerpt}
                                        </p>
                                        <div className="mt-5 pt-5 border-t border-white/[0.06] flex items-center justify-between gap-3">
                                            <AuthorBlock post={p} small />
                                            <span className="shrink-0 text-[11px] font-code text-zinc-500">
                                                {p.date}
                                            </span>
                                        </div>
                                    </div>
                                </motion.a>
                            ))}
                        </motion.div>
                    </AnimatePresence>

                    {filtered.length === 0 && (
                        <p
                            className="text-center text-sm text-zinc-500 py-16"
                            data-testid="blog-empty"
                        >
                            No posts match — try a different filter or search.
                        </p>
                    )}
                </div>
            </section>

         
        </div>
    );
}

/* ──────────────────────────────────────────────
 *  Tiny presentational helpers
 * ────────────────────────────────────────────── */

const AuthorBlock = ({ post, small }) => (
    <div className="flex items-center gap-2.5">
        <div
            className={`inline-flex items-center justify-center rounded-full bg-gradient-to-br ${post.avatarBg} font-outfit font-bold text-obsidian-300 ${small ? "w-7 h-7 text-[11px]" : "w-9 h-9 text-sm"}`}
            aria-hidden
        >
            {post.avatar}
        </div>
        <div className="leading-tight">
            <div
                className={`font-semibold text-white ${small ? "text-xs" : "text-sm"}`}
            >
                {post.author}
            </div>
            <div className="text-[10px] text-zinc-500 font-code">
                {post.role}
            </div>
        </div>
    </div>
);

const CoverDecor = ({ accent }) => (
    <svg
        viewBox="0 0 600 300"
        preserveAspectRatio="none"
        aria-hidden
        className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen"
    >
        <defs>
            <linearGradient id="g1" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
        </defs>
        <g className={accent}>
            <circle cx="500" cy="60" r="120" fill="url(#g1)" />
            <circle cx="80" cy="240" r="80" fill="url(#g1)" opacity="0.6" />
            <path
                d="M0,220 C150,180 320,260 600,200"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.4"
                strokeWidth="1"
            />
            <path
                d="M0,180 C200,140 350,220 600,160"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.25"
                strokeWidth="1"
            />
            <g
                stroke="currentColor"
                strokeOpacity="0.35"
                strokeWidth="1"
                fill="none"
            >
                <circle cx="500" cy="60" r="40" />
                <circle cx="500" cy="60" r="70" />
            </g>
            <g fill="currentColor" fillOpacity="0.65">
                <circle cx="500" cy="60" r="3" />
                <circle cx="80" cy="240" r="2.5" />
                <circle cx="220" cy="120" r="2" />
                <circle cx="380" cy="200" r="2" />
            </g>
            <g
                stroke="currentColor"
                strokeOpacity="0.18"
                strokeWidth="1"
                fill="none"
            >
                <line x1="500" y1="60" x2="220" y2="120" />
                <line x1="500" y1="60" x2="380" y2="200" />
                <line x1="80" y1="240" x2="220" y2="120" />
            </g>
        </g>
        <g className={accent} opacity="0.4">
            <BookOpen />
        </g>
    </svg>
);
